import type {
  STUser,
  UserDetailsDataPayload,
  UserJoint,
  UpdateVisitDetailsPayload,
  UserAnswersPayload,
  UserAnswersResponse,
  STClientAPIAccess,
  GetUserPrefLanguagePayload,
} from "@stss/types";
import axios from "axios";
import type { AxiosRequestConfig, AxiosError, RawAxiosRequestHeaders, AxiosResponse } from "axios";

import {
  loginSTUser,
  createAccess,
  getClientAccessByUserEmail,
  getCompanyConfigsByID,
} from "./validation-api";

const ST_API_URL = process.env.NEXT_PUBLIC_ST_API_URL;
const ST_CLIENT_APP_HASH = process.env.NEXT_PUBLIC_ST_CLIENT_APP_HASH;

function getHeaders(): RawAxiosRequestHeaders {
  const headers: RawAxiosRequestHeaders = {
    "Accept-Language": "en",
    "Client-App-Id": "ST_WEBAPP",
    "Client-App-Hash": "b1ed9df7de08cddf7c2389c741804762870179e0",
  };
  // rewrite Client-App-Hash if ST_CLIENT_APP_HASH is provided
  if (ST_CLIENT_APP_HASH) {
    headers["Client-App-Hash"] = ST_CLIENT_APP_HASH;
  }
  return headers;
}

export async function getUserDetails(data: UserDetailsDataPayload): Promise<STUser> {
  try {
    const payload: UserDetailsDataPayload = {
      ...data,
      role: data.role || "provider",
      num_of_sessions: data.num_of_sessions || 5,
    };
    const config: AxiosRequestConfig = {
      method: "POST",
      url: ST_API_URL + "/api/user/getUserDetails",
      headers: getHeaders(),
      data: payload,
    };
    const userResponse = await axios(config);
    if (Array.isArray(userResponse.data.joints)) {
      userResponse.data.joints = userResponse.data.joints.map((joint: UserJoint) => ({
        ...joint,
        id: +joint.id,
      }));
    }
    return Promise.resolve(userResponse.data);
  } catch (e) {
    return Promise.reject(e);
  }
}

export async function getUserDetailsThroughLogin(data: UserDetailsDataPayload): Promise<STUser> {
  try {
    if (!data.stUserEmail) {
      return Promise.reject(new Error("Missed required parameter stUserEmail in getUserDetails method"));
    }
    const access = await getClientAccessByUserEmail(data.stUserEmail);
    const payload: UserDetailsDataPayload = {
      id: data.id,
      username: data.username,
      stUserId: data.stUserId,
      role: data.role || "provider",
      num_of_sessions: data.num_of_sessions || 5,
    };
    const config: AxiosRequestConfig = {
      method: "POST",
      url: access.url + "/api/user/getUserDetails",
      headers: access.headers,
      data: payload,
    };
    const userResponse = await axios(config);
    return Promise.resolve(userResponse.data);
  } catch (e) {
    return Promise.reject(e);
  }
}

export function fetchUserSurveyAnswers(payload: UserAnswersPayload): Promise<UserAnswersResponse> {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/user/surveyAnswers",
    headers: getHeaders(),
    data: { ...payload, role: "provider" },
  };
  return new Promise<UserAnswersResponse>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function getUsersDetails(data: UserDetailsDataPayload): Promise<unknown>[] | Promise<unknown> {
  if (Array.isArray(data.stUserId)) {
    const promises: Promise<unknown>[] = [];
    data.stUserId.forEach((id) => promises.push(getUserDetails({ ...data, stUserId: id })));
    return Promise.all(promises);
  } else {
    return getUserDetails(data);
  }
}

export function getAllJoints(): Promise<UserJoint[]> {
  const config: AxiosRequestConfig = {
    method: "GET",
    url: ST_API_URL + "/api/locale/joints",
    headers: getHeaders(),
  };
  return new Promise<UserJoint[]>((resolve, reject) => {
    axios(config)
      .then((response) => {
        const joints =
          response.data.joints && Array.isArray(response.data.joints) ? response.data.joints : response.data;
        resolve(joints.map((el: UserJoint) => ({ ...el, id: +el.id })));
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export type STUserResponse = {
  stUserId: null | number;
  errorMessage: string;
};

export function getSTUserByEmail(email: string, access: STClientAPIAccess): Promise<{ user_id: number }> {
  if (!access) {
    throw new Error("Missed required parameter access in getSTUserByEmail method");
  }
  const { url, headers } = access;
  const config: AxiosRequestConfig = {
    method: "POST",
    url: url + "/api/user/getUserIdByEmail",
    headers: headers,
    data: {
      email,
    },
  };
  return new Promise<{ user_id: number }>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e: AxiosError) => {
        reject(e);
      });
  });
}

export async function validateSTUserByEmail(
  email: string,
  access: STClientAPIAccess
): Promise<STUserResponse> {
  const responseData: STUserResponse = {
    stUserId: null,
    errorMessage: "Please enter the email that you used during registration_",
  };
  try {
    const stUserResponse: { user_id: number } = await getSTUserByEmail(email, access);
    responseData.stUserId = stUserResponse.user_id;
    return responseData;
  } catch (e: unknown) {
    const error = e as AxiosError;
    const errorData: AxiosResponse["data"] = error.response?.data;
    if (errorData) {
      const responseMessage = errorData.error ? errorData.error : errorData.message;
      if (responseMessage && typeof responseMessage === "string") {
        responseData.errorMessage = responseMessage;
      }
    } else {
      responseData.errorMessage = "User validation error.";
    }
    return responseData;
  }
}

export async function updateVisitDetailsWithAccess(
  access: STClientAPIAccess,
  payload: UpdateVisitDetailsPayload
): Promise<{ success: boolean }> {
  const { url, headers } = access;
  console.log(">>>>> update visit details payload", payload);
  const config: AxiosRequestConfig = {
    method: "POST",
    headers: headers,
    data: payload,
    url: url + "/api/stss/updateVisitDetails",
  };
  return axios(config)
    .then((res) => {
      res.data;
    })
    .catch((err) => err);
}

export async function updateVisitDetailsWithUserEmail(
  userEmail: string,
  payload: UpdateVisitDetailsPayload
): Promise<{ success: boolean }> {
  try {
    const loginResponse = await loginSTUser(userEmail);
    if (!loginResponse.email_exists || !loginResponse.data?.client_id) {
      return Promise.reject(new Error("User not found or client ID missing"));
    }
    const configs = await getCompanyConfigsByID(loginResponse.data.client_id);
    const access = createAccess(configs);
    return updateVisitDetailsWithAccess(access, payload);
  } catch (e) {
    return Promise.reject(e); // Ensure the function properly returns a rejected Promise
  }
}

export async function getUserPrefLanguage(payload: GetUserPrefLanguagePayload, access: STClientAPIAccess) {
  if (!access) {
    throw new Error("Missed required parameter access in getUserPrefLanguage method");
  }
  const { url, headers } = access;
  const config: AxiosRequestConfig = {
    method: "POST",
    headers: headers,
    data: payload,
    url: url + "/api/user/getUserPrefLang",
  };
  return axios(config)
    .then((res) => res.data)
    .catch((err) => err);
}

export type GetUsersByPtIdPayload = {
  id: number;
  username: string;
  limit: number;
  page: number;
  search: string;
};

export const getUsersByPtId = async <T>(payload: GetUsersByPtIdPayload): Promise<T> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/user/getUsersByPtId",
    headers: getHeaders(),
    data: {
      ...payload,
      role: "provider",
    },
  };
  try {
    const response = await axios(config);
    return Promise.resolve(response.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getVideoIdsPerJoint = async <T>(payload: {
  id: number;
  username: string;
  joint_id: number;
}): Promise<T> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/session/getVideoIdsPerJoint",
    headers: getHeaders(),
    data: {
      ...payload,
      role: "provider",
    },
  };
  try {
    const response = await axios(config);
    return Promise.resolve(response.data.videos);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const overrideSession = async <P, R>(payload: P): Promise<R> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/user/overrideSession",
    headers: getHeaders(),
    data: payload,
  };
  try {
    const response = await axios(config);
    return Promise.resolve(response.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getOverridesByPtId = async <Response>(payload: {
  id: number;
  username: string;
  status: string;
  page?: number;
  limit?: number;
  search: string;
}): Promise<Response> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/user/getOverridesByPtId",
    headers: getHeaders(),
    data: {
      ...payload,
      role: "provider",
    },
  };
  try {
    const response = await axios(config);
    return Promise.resolve(response.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getOverrideById = async <T>(payload: {
  id: number;
  username: string;
  override_id: number;
}): Promise<T> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: ST_API_URL + "/api/user/getOverrideById",
    headers: getHeaders(),
    data: {
      ...payload,
      role: "provider",
    },
  };
  try {
    const response = await axios(config);
    return Promise.resolve(response.data);
  } catch (e) {
    return Promise.reject(e);
  }
};
